import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { selectSessionAdvisor } from '../model/selectors';

export function useAdvisorNavigation() {
  const navigate = useNavigate();
  const advisor = useSelector(selectSessionAdvisor);
  const location = useLocation();

  // Helper to preserve admin mode query params across navigation
  const getAdminModeParams = (): string => {
    const searchParams = new URLSearchParams(location.search);
    const adminMode = searchParams.get('adminMode');
    const advisorId = searchParams.get('advisorId');

    if (!adminMode) return '';

    const params = new URLSearchParams();
    params.set('adminMode', adminMode);
    if (advisorId) params.set('advisorId', advisorId);
    return `?${params.toString()}`;
  };

  const navigateToClientsDashboard = () => {
    const clientsPath = `/advisors/${advisor.advisorId}/clients`;
    navigate(clientsPath);
  };

  const navigateToAdvisorProfile = (tab?: 'profile' | 'communication' | 'defaults') => {
    const advisorProfilePath = `/advisors/${advisor.advisorId}/settings`;
    if (tab) {
      navigate(`${advisorProfilePath}?tab=${tab}`);
    } else {
      navigate(advisorProfilePath);
    }
  };

  // const navigateToOpportunitiesDashboard = () => {
  //   const opportunitiesPath = `/advisors/${advisor.advisorId}/opportunities`;
  //   navigate(opportunitiesPath);
  // };

  const navigateToSponsorshipDashboard = () => {
    const sponsorshipPath = `/advisors/${advisor.advisorId}/sponsorship`;
    navigate(sponsorshipPath);
  };

  const navigateToOnboarding = (toCompletion = false) => {
    const onboardingPath = toCompletion
      ? `/advisors/${advisor.advisorId}/onboarding/completion`
      : `/advisors/${advisor.advisorId}/onboarding/basic-info`;
    navigate(onboardingPath);
  };

  const navigateToSeatsDashboard = () => {
    const seatsPath = `/advisors/${advisor.advisorId}/seats${getAdminModeParams()}`;
    navigate(seatsPath);
  };

  const navigateToIntegrations = () => {
    const integrationsPath = `/advisors/${advisor.advisorId}/integrations`;
    navigate(integrationsPath);
  };

  const navigateToCrmFieldMap = (provider: string) => {
    navigate(`/advisors/${advisor.advisorId}/integrations/${provider}/field-map`);
  };

  const navigateToProspects = () => {
    navigate(`/advisors/${advisor.advisorId}/prospects`);
  };

  const navigateToTeamDashboard = () => {
    const teamPath = `/advisors/${advisor.advisorId}/team${getAdminModeParams()}`;
    navigate(teamPath);
  };

  const navigateToUsageDashboard = () => {
    const usagePath = `/advisors/${advisor.advisorId}/usage${getAdminModeParams()}`;
    navigate(usagePath);
  };

  const navigateToAdminPortal = () => {
    const adminPortalPath = `/advisors/${advisor.advisorId}/admin-portal`;
    navigate(adminPortalPath);
  };

  const navigateToMetrics = () => {
    const metricsPath = `/advisors/${advisor.advisorId}/metrics`;
    navigate(metricsPath);
  };

  const isBasePath = (path: string) => {
    return (
      !!path.match(/^\/advisors\/[a-z0-9]{4}-[a-z0-9]{3}$/) ||
      path === `/advisors` ||
      path === `/advisors/`
    );
  };

  return {
    navigateToClientsDashboard,
    navigateToAdvisorProfile,
    navigateToSponsorshipDashboard,
    navigateToOnboarding,
    navigateToSeatsDashboard,
    navigateToIntegrations,
    navigateToCrmFieldMap,
    navigateToProspects,
    navigateToTeamDashboard,
    navigateToUsageDashboard,
    navigateToAdminPortal,
    navigateToMetrics,
    isBasePath,
    currentPath: location.pathname,
  };
}
